import type { ITenantDestination } from '@/entities/tenant-destination'
import { Loader2, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

interface DestinationSelectFieldProps {
  value: string
  onChange: (destinationId: string) => void
  destinations: ITenantDestination[]
  isLoading: boolean
  disabled: boolean
  /** Language the destination names are shown in. */
  languageCode: string
  /** Details of the chosen destination, shown as a hint under the select. */
  selectedDestination: ITenantDestination | undefined
  onCreateDestination: () => void
}

/** Required "destination" select of the POI form, with an empty state and a shortcut to create one. */
export function DestinationSelectField({
  value,
  onChange,
  destinations,
  isLoading,
  disabled,
  languageCode,
  selectedDestination,
  onCreateDestination,
}: DestinationSelectFieldProps) {
  const { t } = useTranslation()
  const createLabel = t('poi.destinationCreateAction', { defaultValue: 'Create new destination' })
  const selectedHint =
    Object.values(selectedDestination?.localizedData ?? {})[0]?.description ??
    selectedDestination?.destinationId ??
    ''

  return (
    <div className="mb-6 space-y-1.5">
      <label className="text-sm font-medium text-zinc-700">
        {t('poi.destination')} <span className="text-red-500">*</span>
      </label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-11">
          <SelectValue
            placeholder={t('poi.selectDestination', { defaultValue: 'Select destination' })}
          />
        </SelectTrigger>
        <SelectContent>
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 px-2 py-6 text-sm text-zinc-500">
              <Loader2 size={14} className="animate-spin" />
            </div>
          ) : destinations.length === 0 ? (
            <div className="space-y-3 px-3 py-5 text-center">
              <div className="space-y-1">
                <p className="text-sm font-medium text-zinc-800">
                  {t('poi.destinationEmptyTitle', { defaultValue: 'No tenant destinations yet' })}
                </p>
                <p className="text-xs leading-5 text-zinc-500">
                  {t('poi.destinationEmptyDescription', {
                    defaultValue: 'Create a destination first to link it here.',
                  })}
                </p>
              </div>
              <Button type="button" size="sm" className="h-8" onClick={onCreateDestination}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {createLabel}
              </Button>
            </div>
          ) : (
            destinations.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.localizedData?.[languageCode]?.name ?? 'No content available'}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {!isLoading && destinations.length > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 w-full justify-start text-sm font-medium text-blue-600 hover:text-blue-700"
          onClick={onCreateDestination}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{createLabel}</span>
        </Button>
      )}
      {selectedDestination && <p className="text-xs text-zinc-500">{selectedHint}</p>}
    </div>
  )
}
